import Holidays from 'date-holidays';
import { CalculateDueDateBitField } from 'src/bitFields/CalculateDueDateBitField.js'; // Assuming this path is correct

export interface CalculateDueDateTimezoneAgnosticDate {
  utcDay: number;
  utcHour: number; // Optional in input, but always present in output & internal Date objects
  utcMonth: number; // 0-indexed, like JavaScript Date.getUTCMonth()
  utcYear: number;
}

export type CalculateDueDateUnit = 'days' | 'hours' | 'months' | 'weeks';

export interface CalculateDueDateMetrics {
  skippedNationalHolidays: {
    date: Date;
    name: string;
  }[];
  skippedSaturdays: Date[];
  skippedSundays: Date[];
}

export class CalculateDueDate {
  // Constants for configuration and magic numbers
  private static readonly DEFAULT_UTC_HOUR = 0;

  private static readonly HOLIDAY_HOUR_THRESHOLD_UTC = 14; // Skip holidays starting at or before this UTC hour

  private static readonly WORKING_DAYS_SAFETY_ITERATION_MULTIPLIER = 7;

  private static readonly WORKING_DAYS_SAFETY_ITERATION_BUFFER = 30;

  private static readonly FINAL_ADJUSTMENT_MAX_ITERATIONS = 30;

  private readonly metrics: CalculateDueDateMetrics = {
    skippedSaturdays: [],
    skippedSundays: [],
    skippedNationalHolidays: [],
  };

  public static calculateDueDate(
    startDate: {
      utcDay: number;
      utcHour?: number;
      utcMonth: number;
      utcYear: number;
    },
    args: {
      timeToDueUnit: CalculateDueDateUnit;
      timeToDueValue: number;
    },
    flags: number,
    country: string = 'BR',
  ): CalculateDueDateTimezoneAgnosticDate {
    return new CalculateDueDate({
      ...startDate,
      utcHour: startDate.utcHour ?? CalculateDueDate.DEFAULT_UTC_HOUR,
    })
      .withArgs({
        unit: args.timeToDueUnit,
        value: args.timeToDueValue,
      })
      .withFlags(flags)
      .withCountry(country)
      .calculate();
  }

  private readonly startDate: CalculateDueDateTimezoneAgnosticDate;

  private args: { unit: CalculateDueDateUnit; value: number };

  private flags: number;

  private country: string;

  private language: string;

  public constructor(startDate: CalculateDueDateTimezoneAgnosticDate) {
    this.startDate = {
      // Ensure utcHour is set
      ...startDate,
      utcHour: startDate.utcHour ?? CalculateDueDate.DEFAULT_UTC_HOUR,
    };
    this.args = { unit: 'days', value: 0 }; // Default args
    this.flags = 0; // Default flags
    this.country = 'BR'; // Default country
    this.language = 'pt-BR'; // Default language
  }

  public withArgs(args: { unit: CalculateDueDateUnit; value: number }): this {
    this.args = args;
    return this;
  }

  public withFlags(flags: number): this {
    this.flags = flags;
    return this;
  }

  /**
   * @param country - The country code (e.g., 'BR' for Brazil)
   * @param language - ISO 639-1 code for the language (e.g., 'pt-BR' for Brazilian Portuguese)
   */
  public withCountry(country: string, language?: string): this {
    this.country = country;
    this.language = language ?? 'pt-BR'; // Default to Brazilian Portuguese
    return this;
  }

  public calculate(): CalculateDueDateTimezoneAgnosticDate & {
    metrics: CalculateDueDateMetrics;
  } {
    console.log('[CalculateDueDate] Calculation started with:', {
      startDate: this.startDate,
      args: this.args,
      flags: this.flags,
      country: this.country,
    });

    this.resetMetrics();

    // Guard clauses for invalid arguments
    if (!this.args.unit || this.args.value === null || this.args.value === undefined) {
      console.log('[CalculateDueDate] Invalid unit or value. Returning start date.');
      return this.returnStartDate();
    }

    if (this.args.value <= 0) {
      console.log('[CalculateDueDate] Non-positive value. Returning start date.');
      return this.returnStartDate();
    }

    const flagsBitField = new CalculateDueDateBitField(this.flags);
    const skipWeekend = flagsBitField.has(CalculateDueDateBitField.FLAGS.SKIP_WEEKEND);
    const skipNationalHoliday = flagsBitField.has(CalculateDueDateBitField.FLAGS.SKIP_NATIONAL_HOLIDAY);

    const baseDate = this.getBaseDate();
    const holidaysHandler = new Holidays(this.country, {
      languages: [this.language],
    });

    let resultDate: Date;

    // Calculate the initial result date based on unit and value
    if (this.args.unit === 'days') {
      resultDate = this.addWorkingDays(baseDate, this.args.value, skipWeekend, skipNationalHoliday, holidaysHandler);
    } else {
      // Handles 'hours', 'weeks', 'months'
      resultDate = this.addTimeUnits(baseDate, this.args.unit, this.args.value);
    }

    // Adjust the result date to ensure it's a working day if flags are set
    const finalAdjustedDate = this.adjustToWorkingDayIfNecessary(resultDate, skipWeekend, skipNationalHoliday, holidaysHandler);

    console.log('[CalculateDueDate] Final adjusted UTC date:', finalAdjustedDate);
    console.log('[CalculateDueDate] Final adjusted date metrics:', this.metrics);

    return {
      utcDay: finalAdjustedDate.getUTCDate(),
      utcMonth: finalAdjustedDate.getUTCMonth(), // 0-indexed
      utcYear: finalAdjustedDate.getUTCFullYear(),
      utcHour: finalAdjustedDate.getUTCHours(),
      metrics: this.metrics,
    };
  }

  private returnStartDate(): CalculateDueDateTimezoneAgnosticDate & {
    metrics: CalculateDueDateMetrics;
  } {
    // Ensure utcHour is always present in the returned object
    return {
      utcDay: this.startDate.utcDay,
      utcMonth: this.startDate.utcMonth,
      utcYear: this.startDate.utcYear,
      utcHour: this.startDate.utcHour ?? CalculateDueDate.DEFAULT_UTC_HOUR,
      metrics: this.metrics,
    };
  }

  private getBaseDate(): Date {
    const baseDate = new Date(Date.UTC(this.startDate.utcYear, this.startDate.utcMonth, this.startDate.utcDay, this.startDate.utcHour));
    console.log('[CalculateDueDate] Base date in UTC:', { baseDate });
    return baseDate;
  }

  /**
   * Adds a specified number of working days to a base date.
   * Skips weekends and/or national holidays based on flags.
   */
  protected addWorkingDays(
    baseDate: Date,
    daysToAdd: number,
    skipWeekend: boolean,
    skipNationalHoliday: boolean,
    holidaysHandler: Holidays,
  ): Date {
    let currentDate = new Date(baseDate.getTime());
    console.log('[CalculateDueDate.addWorkingDays] Adding working days:', { daysToAdd, skipWeekend, skipNationalHoliday, baseDate });

    let workingDaysCounted = 0;
    // Safety counter to prevent infinite loops in unexpected scenarios (e.g., all days are holidays)
    let maxIterations =
      daysToAdd * CalculateDueDate.WORKING_DAYS_SAFETY_ITERATION_MULTIPLIER + CalculateDueDate.WORKING_DAYS_SAFETY_ITERATION_BUFFER;

    while (workingDaysCounted < daysToAdd && maxIterations > 0) {
      currentDate = new Date(
        Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate() + 1, currentDate.getUTCHours()),
      );
      maxIterations--;

      let isNonWorkingDay = false;
      if (skipWeekend && this.isUTCSaturday(currentDate)) {
        console.log('[CalculateDueDate.addWorkingDays] Skipping weekend:', { currentDate });
        this.metrics.skippedSaturdays.push(currentDate);
        isNonWorkingDay = true;
      }

      if (!isNonWorkingDay && skipWeekend && this.isUTCSunday(currentDate)) {
        console.log('[CalculateDueDate.addWorkingDays] Skipping weekend:', { currentDate });
        this.metrics.skippedSundays.push(currentDate);
        isNonWorkingDay = true;
      }

      const [isConsideredHoliday, holidayName] = this.isConsideredNonWorkingHoliday(currentDate, holidaysHandler);

      if (!isNonWorkingDay && skipNationalHoliday && isConsideredHoliday) {
        console.log('[CalculateDueDate.addWorkingDays] Skipping national holiday:', { currentDate });
        this.metrics.skippedNationalHolidays.push({ date: currentDate, name: holidayName ?? 'Unknown' });
        isNonWorkingDay = true;
      }

      if (!isNonWorkingDay) {
        workingDaysCounted++;
        console.log('[CalculateDueDate.addWorkingDays] Counted working day:', { workingDaysCounted, currentDate });
      }
    }

    if (maxIterations === 0) {
      console.warn('[CalculateDueDate.addWorkingDays] Safety iteration limit reached. Result might be inaccurate.');
    }

    return currentDate;
  }

  /**
   * Adds time in units other than days (hours, weeks, months).
   */
  protected addTimeUnits(baseDate: Date, unit: Exclude<CalculateDueDateUnit, 'days'>, value: number): Date {
    let resultDate = new Date(baseDate.getTime());
    switch (unit) {
      case 'hours':
        resultDate = new Date(resultDate.getTime() + value * 60 * 60 * 1_000);
        console.log('[CalculateDueDate.addTimeUnits] Added hours:', { hours: value, resultDate });
        break;
      case 'weeks':
        resultDate = new Date(
          Date.UTC(resultDate.getUTCFullYear(), resultDate.getUTCMonth(), resultDate.getUTCDate() + value * 7, resultDate.getUTCHours()),
        );
        console.log('[CalculateDueDate.addTimeUnits] Added weeks:', { weeks: value, resultDate });
        break;
      case 'months':
        // Date.UTC handles month overflow correctly (e.g., month 12 becomes January of next year)
        resultDate = new Date(
          Date.UTC(resultDate.getUTCFullYear(), resultDate.getUTCMonth() + value, resultDate.getUTCDate(), resultDate.getUTCHours()),
        );
        console.log('[CalculateDueDate.addTimeUnits] Added months:', { months: value, resultDate });
        break;
      // No default needed due to `Exclude` and previous checks, but good for safety if types were looser.
      // default:
      //   console.warn(`[CalculateDueDate.addTimeUnits] Unexpected unit: ${unit}`);
    }

    return resultDate;
  }

  /**
   * Adjusts a date to the next working day if it falls on a weekend or holiday, based on flags.
   */
  protected adjustToWorkingDayIfNecessary(date: Date, skipWeekend: boolean, skipNationalHoliday: boolean, holidaysHandler: Holidays): Date {
    let adjustedDate = new Date(date.getTime());
    let maxAdjustments = CalculateDueDate.FINAL_ADJUSTMENT_MAX_ITERATIONS; // Safety break for chained non-working days

    while (maxAdjustments > 0) {
      let wasAdjustedInThisIteration = false;

      // Adjust for weekends first
      if (skipWeekend && (this.isUTCSaturday(adjustedDate) || this.isUTCSunday(adjustedDate))) {
        console.log('[CalculateDueDate.adjustToWorkingDay] Adjusting from weekend:', { adjustedDate });
        adjustedDate = this.moveToNextUTCMonday(adjustedDate);
        console.log('[CalculateDueDate.adjustToWorkingDay] Adjusted to Monday:', { adjustedDate });
        wasAdjustedInThisIteration = true;

        if (this.isUTCSaturday(adjustedDate)) {
          this.metrics.skippedSaturdays.push(adjustedDate);
        } else {
          this.metrics.skippedSundays.push(adjustedDate);
        }
      }

      const [isConsideredHoliday, holidayName] = this.isConsideredNonWorkingHoliday(adjustedDate, holidaysHandler);

      // Then, adjust for national holidays (could be the new Monday or the original date if not a weekend)
      if (skipNationalHoliday && isConsideredHoliday) {
        console.log('[CalculateDueDate.adjustToWorkingDay] Adjusting from national holiday:', { adjustedDate });
        adjustedDate = new Date(
          Date.UTC(adjustedDate.getUTCFullYear(), adjustedDate.getUTCMonth(), adjustedDate.getUTCDate() + 1, adjustedDate.getUTCHours()),
        );
        console.log('[CalculateDueDate.adjustToWorkingDay] Adjusted to next day:', { adjustedDate });
        this.metrics.skippedNationalHolidays.push({ date: adjustedDate, name: holidayName ?? 'Unknown' });
        wasAdjustedInThisIteration = true;
      }

      if (!wasAdjustedInThisIteration) {
        break; // Date is a working day according to flags
      }

      maxAdjustments--;
    }

    if (maxAdjustments === 0) {
      console.warn('[CalculateDueDate.adjustToWorkingDay] Reached maximum iterations. Date might still be non-working.');
    }

    return adjustedDate;
  }

  /**
   * Checks if a given date is a national holiday that should be skipped for work purposes.
   * A holiday is skipped if:
   * 1. It's a registered holiday.
   * 2. Not ALL entries for that holiday are of type 'observance'.
   * 3. At least one holiday entry (any type) for that day starts at or before HOLIDAY_HOUR_THRESHOLD_UTC.
   */
  protected isConsideredNonWorkingHoliday(date: Date, holidaysHandler: Holidays): [boolean, string | null] {
    const holidayInfo = holidaysHandler.isHoliday(date);

    if (!holidayInfo || holidayInfo.length === 0) {
      return [false, null]; // Not a holiday period.
    }

    console.log('[CalculateDueDate.isConsideredNonWorkingHoliday] Holiday info:', { date, holidayInfo });

    // Condition 1: It must not be solely 'observance' type holidays.
    const isSubstantiveHoliday = !holidayInfo.every((holi) => holi.type?.includes('observance'));
    if (!isSubstantiveHoliday) {
      console.log('[CalculateDueDate.isConsideredNonWorkingHoliday] Holiday is observance only, not treated as non-working:', {
        date,
        holidayInfo,
      });
      return [false, null];
    }

    // Condition 2: At least one holiday entry for that day must start at or before the defined UTC hour threshold.
    const hasEarlyActivity = holidayInfo.some(
      (holiday) => new Date(holiday.date).getUTCHours() <= CalculateDueDate.HOLIDAY_HOUR_THRESHOLD_UTC,
    );

    if (hasEarlyActivity) {
      console.log('[CalculateDueDate.isConsideredNonWorkingHoliday] Date is a skippable national holiday:', { date, holidayInfo });
      return [true, holidayInfo[0]!.name];
    } else {
      console.log(
        '[CalculateDueDate.isConsideredNonWorkingHoliday] Date is a substantive holiday but does not meet early activity threshold, treated as working:',
        { date, holidayInfo },
      );
      return [false, null];
    }
  }

  protected isUTCSaturday(date: Date): boolean {
    return date.getUTCDay() === 6;
  }

  protected isUTCSunday(date: Date): boolean {
    return date.getUTCDay() === 0;
  }

  /**
   * Given a date that is a UTC Saturday or Sunday, returns the following UTC Monday.
   */
  protected moveToNextUTCMonday(date: Date): Date {
    // Precondition: 'date' is currently a UTC Saturday or Sunday.
    const dayOfWeek = date.getUTCDay(); // 0 for Sunday, 6 for Saturday
    const daysToAdd = dayOfWeek === 6 ? 2 : 1; // If Saturday, add 2 days; if Sunday, add 1 day.
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + daysToAdd, date.getUTCHours()));
  }

  public getMetrics() {
    return this.metrics;
  }

  private resetMetrics() {
    this.metrics.skippedNationalHolidays = [];
    this.metrics.skippedSaturdays = [];
    this.metrics.skippedSundays = [];
  }
}
